import React, { forwardRef, useCallback, useMemo } from 'react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import { isToday } from 'date-fns'

import styled from 'styled-components'

// theme
import aampeTheme from '../../../constants/aampeTheme'
// constants
import ICONS from '../../../constants/icons'

// helper
import { formatDayMonth } from '../../../helpers/dateTimeHelpers'

// components
import CalendarHeader from './CustomCalendarHeader'
import './css/datepicker.css'

const AampeDatePicker = ({
    selectedDate,
    setSelectedDate,
    placeholder = '',
    minDate,
    maxDate,
    isClearable,
    isDisabled = false,
    dateFormat = 'MMM d, yyyy',
    placeholderColor = aampeTheme.colors.neutral09,
    textStyle = {},
    selectsRange = false,
    startDate = null,
    endDate = null,
}) => {
    const pickerRef = React.useRef(null)

    const CustomInput = useMemo(() => {
        return forwardRef((props, ref) => {
            const [startDateStr, endDateStr] = props.value.split(' - ')
            let displayEndText = endDateStr
            if (endDate && isToday(endDate)) {
                if (endDateStr === formatDayMonth(endDate)) {
                    displayEndText = 'Today'
                }
            }
            const displayText = startDateStr + ' - ' + displayEndText
            return (
                <TextFieldContainer onClick={props.onClick}>
                    <AampeTextField
                        onChange={() => {}}
                        value={
                            selectsRange && startDate && endDate
                                ? displayText
                                : props.value
                        }
                        ref={ref}
                        placeholder={placeholder}
                        disabled={isDisabled}
                        style={textStyle}
                    />
                    <div>
                        <StyledCalendarIcon />
                    </div>
                </TextFieldContainer>
            )
        })
    }, [placeholder, isDisabled, selectsRange, startDate, endDate])

    const ClearableFooter = useCallback(
        ({ children }) => {
            const handleClear = () => {
                setSelectedDate(null)
                pickerRef.current.setOpen(false)
            }
            return (
                <CalendarWrapper>
                    {children}
                    <CalendarFooter>
                        <Divider>OR</Divider>
                        <CalendarButton onClick={() => handleClear()}>
                            Clear Date
                        </CalendarButton>
                    </CalendarFooter>
                </CalendarWrapper>
            )
        },
        [setSelectedDate],
    )

    return (
        <DatePicker
            className={'calendar-date-input'}
            ref={pickerRef}
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat={dateFormat}
            customInput={<CustomInput />}
            calendarContainer={isClearable ? ClearableFooter : null}
            renderCustomHeader={CalendarHeader}
            minDate={minDate}
            maxDate={maxDate}
            disabled={isDisabled}
            placeholderColor={placeholderColor}
            selectsRange={selectsRange}
            startDate={startDate}
            endDate={endDate}
        />
    )
}

export default AampeDatePicker

const AampeTextField = styled.input((props) => ({
    outline: 'none',
    border: 'none',
    cursor: props.disabled ? 'unset' : 'pointer',
    '&::placeholder': {
        color: props.placeholderColor,
    },
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.medium,
    lineHeight: aampeTheme.size.lineHeight.default,
    color: aampeTheme.colors.neutral09,
    width: '100%',
    flexWrap: 'nowrap',
    display: 'flex',
}))

const CalendarWrapper = styled.div({
    background: aampeTheme.colors.white,
    display: 'flex',
    flexFlow: 'column',
    borderRadius: aampeTheme.borderRadius.default,
    border: `1px solid ${aampeTheme.colors.backgroundWG02}`,
    padding: aampeTheme.spacing(2, 2, 0, 2),
    boxShadow:
        '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
})

const CalendarFooter = styled.div({
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'center',
})

const Divider = styled.div({
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    borderBottom: `1px solid ${aampeTheme.colors.bordersWG01}`,
    padding: aampeTheme.spacing(1),
    color: aampeTheme.colors.bordersWG01,
})

const CalendarButton = styled.button({
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.backgroundWG01,
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    margin: aampeTheme.spacing(1, 2),
    padding: aampeTheme.spacing(1, 2),
    flexGrow: 1,
    color: aampeTheme.colors.gray800,
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: aampeTheme.colors.bordersWG01,
    },
    '&:active': {
        backgroundColor: aampeTheme.colors.solidA01,
        color: aampeTheme.colors.white,
    },
})

const TextFieldContainer = styled.div({
    display: 'flex',
    alignItems: 'center',
    borderRadius: aampeTheme.borderRadius.default,
    border: `1px solid ${aampeTheme.colors.interactiveWG01}`,
    backgroundColor: aampeTheme.colors.white,
    height: '36px',
    minWidth: '144px',
    '&:hover': {
        border: `1px solid ${aampeTheme.colors.textAccessibleWG01}`,
    },
    padding: aampeTheme.spacing4(1, 2, 1, 3),
    gap: aampeTheme.spacing4(2),
})

const StyledCalendarIcon = styled(ICONS.calendar)({
    color: aampeTheme.colors.textAccessibleWG01,
    width: '18px',
    height: '18px',
})
